import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import { CommonButton } from '../../Styles/CommonStyles';
import TextField from '@mui/material/TextField';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import useAuth from '../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import { useGetUsersQuery } from '../../features/users/usersApiSlice';
import { useGetSupportUsersQuery } from '../../features/supportusers/supportusersApiSlice';
import { useGetStoreOfficesQuery } from '../../features/storeoffices/storeofficesApiSlice';
import nameStringFn from '../../data/nameStringFn';
import ComplaintActionOptions from '../../data/ComplaintActionOptions';
import moment from 'moment';

// Import role-specific form components
import UserComplaintForm from './Forms/UserComplaintForm';
import SupportStaffComplaintForm from './Forms/SupportStaffComplaintForm';
import SupervisorComplaintForm from './Forms/SupervisorComplaintForm';
import SupportAdminComplaintForm from './Forms/SupportAdminComplaintForm';

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', sans-serif;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const BackButton = styled.div`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  color: #1976d2;

  &:hover {
    text-decoration: underline;
  }
`;

const Button = styled(CommonButton)`
  padding: 12px 20px;
  min-width: 140px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
`;

const ActionButton = styled(Button)`
  background-color: #1976d2;
`;

const ActionContainer = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ComplaintDetailsContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #e8e8e8;
`;

const ComplaintTitle = styled.h2`
  margin-bottom: 24px;
  color: #1976d2;
  font-size: 1.5rem;
  font-weight: 600;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 32px 48px;
  margin-bottom: 24px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 0;
  border-bottom: 1px solid #e8e8e8;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-size: 0.75rem;
  color: #666;
  margin-bottom: 4px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const DetailValue = styled.span`
  font-size: 0.95rem;
  color: #333;
  font-weight: 500;
  line-height: 1.4;
  word-break: break-word;
`;

const Description = styled.div`
  margin-top: 24px;
  padding: 20px;
  background: linear-gradient(135deg, #f3f8ff 0%, #ffffff 100%);
  border: 1px solid #e3f2fd;
  border-radius: 12px;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(135deg, #1976d2, #42a5f5);
    border-radius: 2px 0 0 2px;
  }
  
  ${DetailLabel} {
    margin-bottom: 12px;
    color: #1976d2;
    font-size: 0.9rem;
  }
  
  ${DetailValue} {
    color: #444;
    line-height: 1.6;
  }
`;

const FlowTitle = styled.h3`
  margin: 0 0 15px 0;
  color: #333;
  font-size: 18px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 10px;
`;

const TimelineContainer = styled.div`
  margin-top: 24px;
  padding-left: 16px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 2px;
    background-color: #e0e0e0;
    z-index: 0;
  }
`;

const TimelineItemContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
  
  &::before {
    content: '';
    position: absolute;
    left: -20px;
    top: 24px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #1976d2;
    z-index: 1;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TimelineCard = styled.div`
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  padding: 20px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  background-color: ${props => {
    if (props.status === 'rejected') return '#fff8f8';
    if (props.status === 'closed') return '#f1f8e9';
    return props.isEven ? '#f8f9fa' : '#fff';
  }};
  border-left: 3px solid ${props => {
    if (props.status === 'pending') return '#f57c00';
    if (props.status === 'rejected') return '#d32f2f';
    if (props.status === 'closed') return '#4caf50';
    return '#388e3c';
  }};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 8px rgba(0,0,0,0.1);
  }
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
`;

const TimelineTitle = styled.div`
  font-size: 17px;
  font-weight: 500;
  color: #202124;
  letter-spacing: -0.01em;
`;

const TimelineTime = styled.div`
  font-size: 14px;
  color: #5f6368;
`;

const StatusBadge = styled.div`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  background: ${props => {
    if (props.status === 'closed') return '#e8f5e9';
    if (props.status === 'pending') return '#fff8f1';
    if (props.status === 'rejected') return '#ffebee';
    return '#f5f5f5';
  }};
  color: ${props => {
    if (props.status === 'closed') return '#2e7d32';
    if (props.status === 'pending') return '#ed6c02';
    if (props.status === 'rejected') return '#d32f2f';
    return '#757575';
  }};
`;

const UserInfo = styled.div`
  margin-top: 15px;
  padding: 10px;
  background: #e3f2fd;
  border-radius: 4px;
  font-size: 14px;
  
  & > div {
    margin-bottom: 5px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    strong {
      font-weight: 600;
      margin-right: 8px;
    }
  }
`;

const Remark = styled.div`
  margin: 15px 0;
  padding: 15px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  
  h4 {
    margin: 0 0 10px 0;
    color: #333;
    font-size: 14px;
    font-weight: 600;
  }
  
  div {
    font-size: 14px;
    line-height: 1.5;
    color: #555;
  }
`;

const ComplaintDetails = ({ row, subTabValue, setChangepage }) => {
  const [remark, setRemark] = useState('');
  const [showActionOptions, setShowActionOptions] = useState(false);
  const [userRole, setUserRole] = useState("user");
  const [loading, setLoading] = useState(false);

  const auth = useAuth();
  const user_current_office = useSelector(state => state.office.off);
  console.log(row)
  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({
      getSingleuser: data?.entities
    }),
  });

  console.log(getSingleuser)

  const { supportusers } = useGetSupportUsersQuery('supportusers', {
    selectFromResult: ({ data }) => ({
      supportusers: data?.entities
    }),
  });

  const { storeoffices } = useGetStoreOfficesQuery("storeOffList", {
    selectFromResult: ({ data }) => ({
      storeoffices: data?.entities
    }),
  });


  const handleBack = () => {
    setChangepage([true, false, false, false, false]);
  };

  const handleTakeAction = () => {
    setShowActionOptions(true);
  };

  // Helper function to check if complaint is currently with support staff (not escalated to supervisor)
  const isComplaintWithSupportStaff = () => {
    // Tags that indicate complaint is with support staff
    const supportStaffTags = [
      'complaint_created',
      'fwd_reaasign_ss',
      'fwd_to_oldss_foraction',
      'compl_on_hold'
    ];
    return supportStaffTags.includes(row?.curr_sent_to_tag);
  };

  // Helper function to check if this complaint is for the current user to action
  const isComplaintForUser = () => {
    if (subTabValue === 6) {
      return false;
    }

    // For user inbox (subTabValue 0): allow if user is the original complaint creator
    // and the complaint is closed (for escalation within the 4-day window)
    if (subTabValue === 0 && row?.curr_status === 'Closed') {
      const originalCreatorId = row?.req_data?.[0]?.sent_by?.id || row?.req_creator_id;
      return originalCreatorId === auth?.id;
    }

    if (subTabValue === 3) {
      if (auth?.supportuser && isComplaintWithSupportStaff()) {
        return true;
      }
      return false;
    }

    if (row?.curr_sent_to === auth?.id) {
      return true;
    }

    // L2Support backup: can act on complaints with L1 tags in their store
    if (subTabValue === 5 && storeoffices) {
      const userStore = Object.values(storeoffices).find(
        store => store.roleUsers?.L2Support === auth?.id && store.storeoffice === row?.store
      );
      const l1Tags = ['escalated_to_l1', 'supv_resolved_fwd_l1'];
      const hasL1Tag = l1Tags.includes(row?.curr_sent_to_tag);
      if (userStore && hasL1Tag) {
        return true;
      }
    }

    if (row?.req_data && row.req_data.length > 0) {
      const lastAction = row.req_data[row.req_data.length - 1];
      return lastAction.sent_to?.id === auth?.id;
    }

    if (row?.flow && row.flow.length > 0) {
      const lastFlow = row.flow[row.flow.length - 1];
      return lastFlow.forwardedTo === auth?.id;
    }

    if (row?.assignedTo === auth?.id) {
      return true;
    }

    return false;
  };

  // Check if the complaint is closed
  const isComplaintClosed = () => {
    if (row?.curr_status === 'Closed') return true;

    if (row?.req_data && row.req_data.length > 0) {
      const lastAction = row.req_data[row.req_data.length - 1];
      return lastAction.action === 'Closed' || lastAction.sent_to_tag === 'Closed';
    }

    if (row?.flow && row.flow.length > 0) {
      const lastFlow = row.flow[row.flow.length - 1];
      return lastFlow.status === 'Closed';
    }

    return false;
  };

  // Get available actions for the current user role
  const getAvailableActions = () => {
    if (subTabValue === 1) {
      return ComplaintActionOptions.SupportStaff || [];
    }
    if (subTabValue === 2) {
      // If complaint has been escalated, supervisor cannot close -- only forward to L1
      const escalationCount = row?.escalation_count || 0;
      if (escalationCount >= 1) {
        return ComplaintActionOptions.SupervisorEscalated || [];
      }
      return ComplaintActionOptions.Supervisor || [];
    }
    if (subTabValue === 0) return ComplaintActionOptions.user || [];
    if (subTabValue === 3 && isComplaintWithSupportStaff()) return ComplaintActionOptions.SupervisorAllComplaints || [];
    if (subTabValue === 4 || subTabValue === 5) {
      return ComplaintActionOptions.L1Support || [];
    }
    return [];
  };



  // Render the appropriate form based on user role
  const renderRoleSpecificForm = () => {
    if (!isComplaintForUser()) {
      return null;
    }

    const availableActions = getAvailableActions();

    switch (subTabValue) {
      case 0: // User Inbox
        return (
          <UserComplaintForm
            row={row}
            remark={remark}
            setRemark={setRemark}
            auth={auth}
            user_current_office={user_current_office}
            getSingleuser={getSingleuser}
            supportusers={supportusers}
            storeoffices={storeoffices}
            handleBack={handleBack}
            availableActions={availableActions}
          />
        );
      case 1: // Support Staff Inbox
        return (
          <SupportStaffComplaintForm
            row={row}
            remark={remark}
            setRemark={setRemark}
            auth={auth}
            user_current_office={user_current_office}
            getSingleuser={getSingleuser}
            supportusers={supportusers}
            handleBack={handleBack}
            availableActions={availableActions}
          />
        );
      case 2: // Supervisor Inbox
        return (
          <SupervisorComplaintForm
            row={row}
            remark={remark}
            setRemark={setRemark}
            auth={auth}
            user_current_office={user_current_office}
            getSingleuser={getSingleuser}
            supportusers={supportusers}
            storeoffices={storeoffices}
            handleBack={handleBack}
            availableActions={availableActions}
          />
        );
      case 3: // Supervisor All Complaints - only reassign action allowed for complaints with support staff
        if (isComplaintWithSupportStaff()) {
          return (
            <SupervisorComplaintForm
              row={row}
              remark={remark}
              setRemark={setRemark}
              auth={auth}
              user_current_office={user_current_office}
              getSingleuser={getSingleuser}
              supportusers={supportusers}
              storeoffices={storeoffices}
              handleBack={handleBack}
              availableActions={availableActions}
            />
          );
        }
        return null;
      case 4: // L1Support Inbox
        return (
          <SupportAdminComplaintForm
            row={row}
            remark={remark}
            setRemark={setRemark}
            auth={auth}
            user_current_office={user_current_office}
            getSingleuser={getSingleuser}
            supportusers={supportusers}
            handleBack={handleBack}
            availableActions={availableActions}
          />
        );
      case 5: // L2Support Inbox
        return (
          <SupportAdminComplaintForm
            row={row}
            remark={remark}
            setRemark={setRemark}
            auth={auth}
            user_current_office={user_current_office}
            getSingleuser={getSingleuser}
            supportusers={supportusers}
            handleBack={handleBack}
            availableActions={availableActions}
          />
        );
      default:
        return null;
    }
  };

  // Helper function to get current status
  const getCurrentStatus = () => {
    if (row?.curr_status) return row.curr_status;
    if (row?.req_data && row.req_data.length > 0) {
      return row.req_data[row.req_data.length - 1]?.action || 'Unknown';
    }
    if (row?.flow && row.flow.length > 0) {
      return row.flow[row.flow.length - 1]?.status || 'Unknown';
    }
    return 'Unknown';
  };

  // Helper function to get creation date
  const getCreationDate = () => {
    if (row?.created_at) return moment(row.created_at).format("DD/MM/YYYY HH:mm");
    if (row?.req_data && row.req_data.length > 0) {
      return moment(row.req_data[0]?.time_stamp).format("DD/MM/YYYY HH:mm");
    }
    if (row?.flow && row.flow.length > 0) {
      return moment(row.flow[0]?.timeStamp).format("DD/MM/YYYY HH:mm");
    }
    return 'N/A';
  };

  // Helper function to get assigned user name
  const getAssignedUserName = () => {
    const assignedId = row?.curr_assigned_to;
    if (!assignedId) return 'N/A';

    // Check in regular users first
    if (getSingleuser && getSingleuser[assignedId]) {
      return nameStringFn(getSingleuser[assignedId]);
    }

    // Check in support users
    if (supportusers && supportusers[assignedId]) {
      return nameStringFn(supportusers[assignedId]);
    }

    // If no name found, return the ID
    return assignedId;
  };

  return (
    <Container>
      <BackButtonWrapper>
        <BackButton onClick={handleBack}>
          <ArrowLeftIcon />
          <span style={{ marginLeft: '8px' }}>Back to Inbox</span>
        </BackButton>
      </BackButtonWrapper>

      {/* Complaint Details Section */}
      <ComplaintDetailsContainer>
        <ComplaintTitle>Complaint Details</ComplaintTitle>

        <DetailsGrid>
          <DetailItem>
            <DetailLabel>Complaint No.</DetailLabel>
            <DetailValue>{row?.complaintNo || row?._id || 'N/A'}</DetailValue>
          </DetailItem>

          <DetailItem>
            <DetailLabel>Registered At</DetailLabel>
            <DetailValue>{getCreationDate()}</DetailValue>
          </DetailItem>

          <DetailItem>
            <DetailLabel>Category</DetailLabel>
            <DetailValue>{row?.complaintCat || 'N/A'}</DetailValue>
          </DetailItem>

          <DetailItem>
            <DetailLabel>Subject</DetailLabel>
            <DetailValue>{row?.complaintSub || 'N/A'}</DetailValue>
          </DetailItem>

          <DetailItem>
            <DetailLabel>Current Status</DetailLabel>
            <DetailValue>{getCurrentStatus()}</DetailValue>
          </DetailItem>

          <DetailItem>
            <DetailLabel>Registered By</DetailLabel>
            <DetailValue>{row?.req_creator_details || row?.registeredBy || 'N/A'}</DetailValue>
          </DetailItem>

          <DetailItem>
            <DetailLabel>Currently Assigned To</DetailLabel>
            <DetailValue>{getAssignedUserName()}</DetailValue>
          </DetailItem>

          <DetailItem>
            <DetailLabel>Asset Name</DetailLabel>
            <DetailValue>{row?.assetName || 'N/A'}</DetailValue>
          </DetailItem>

          <DetailItem>
            <DetailLabel>Store</DetailLabel>
            <DetailValue>{row?.store || 'N/A'}</DetailValue>
          </DetailItem>
        </DetailsGrid>

        {(row?.complaintDesc || row?.Description) && (
          <Description>
            <DetailLabel>Complaint Description</DetailLabel>
            <DetailValue>{row?.complaintDesc || row?.Description}</DetailValue>
          </Description>
        )}
      </ComplaintDetailsContainer>

      {/* Flow History Section */}
      {((row?.req_data && row.req_data.length > 0) || (row?.flow && row.flow.length > 0)) && (
        <div>
          <FlowTitle>Complaint Flow History</FlowTitle>

          <TimelineContainer>
            {/* Handle new req_data structure */}
            {row?.req_data && row.req_data.length > 0 && row.req_data.map((reqItem, index) => (
              <TimelineItemContainer key={index}>
                <TimelineCard
                  isEven={index % 2 === 1}
                  status={reqItem.sent_to_tag === 'Closed' ? 'closed' : 'pending'}
                >
                  <TimelineHeader>
                    <TimelineTitle>Action {index + 1}</TimelineTitle>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <TimelineTime>{moment(reqItem.time_stamp).format("DD/MM/YYYY HH:mm")}</TimelineTime>
                      <StatusBadge status={reqItem.sent_to_tag === 'Closed' ? 'closed' : 'pending'}>
                        {reqItem.sent_to_tag === 'Closed' ? 'CLOSED' : 'PENDING'}
                      </StatusBadge>
                    </div>
                  </TimelineHeader>

                  {reqItem?.action && (
                    <div style={{
                      marginBottom: '15px',
                      padding: '10px 12px',
                      background: reqItem?.sent_to_tag === 'Closed' ? '#e8f5e9' : '#f0f8ff',
                      borderRadius: '4px',
                      borderLeft: `3px solid ${reqItem?.sent_to_tag === 'Closed' ? '#4caf50' : '#1976d2'
                        }`,
                      fontSize: '16px',
                      lineHeight: '1.5',
                      textAlign: 'center',
                      fontWeight: 'bold'
                    }}>
                      {reqItem.action}
                    </div>
                  )}

                  {reqItem.text && (
                    <Remark>
                      <h4>Remark</h4>
                      <div>{reqItem.text}</div>
                    </Remark>
                  )}

                  <UserInfo>
                    <div><strong>From:</strong> {reqItem.sent_by?.name ?
                      `${reqItem.sent_by.name.firstName || ''} ${reqItem.sent_by.name.middleName || ''} ${reqItem.sent_by.name.lastName || ''}`.trim() :
                      'Unknown'
                    } ({reqItem.sent_by?.designation || 'Unknown'})</div>
                    {reqItem.on_behalf_of && (
                      <div style={{
                        padding: '4px 8px',
                        margin: '4px 0',
                        background: '#fff8e1',
                        borderLeft: '3px solid #ff8f00',
                        borderRadius: '2px',
                        fontSize: '13px'
                      }}>
                        <strong>On Behalf Of:</strong> {reqItem.on_behalf_of?.name ?
                          `${reqItem.on_behalf_of.name.firstName || ''} ${reqItem.on_behalf_of.name.middleName || ''} ${reqItem.on_behalf_of.name.lastName || ''}`.trim() :
                          'Unknown'
                        } ({reqItem.on_behalf_of?.designation || 'Unknown'})
                      </div>
                    )}
                    <div><strong>To:</strong> {reqItem.sent_to?.name ?
                      `${reqItem.sent_to.name.firstName || ''} ${reqItem.sent_to.name.middleName || ''} ${reqItem.sent_to.name.lastName || ''}`.trim() :
                      'Unknown'
                    } ({reqItem.sent_to?.designation || 'Unknown'})</div>
                  </UserInfo>
                </TimelineCard>
              </TimelineItemContainer>
            ))}

            {/* Handle old flow structure for backward compatibility */}
            {(!row?.req_data || row.req_data.length === 0) && row?.flow && row.flow.length > 0 && row.flow.map((flowItem, index) => (
              <TimelineItemContainer key={index}>
                <TimelineCard
                  isEven={index % 2 === 1}
                  status={flowItem.status === 'Closed' ? 'closed' : 'pending'}
                >
                  <TimelineHeader>
                    <TimelineTitle>Action {index + 1}</TimelineTitle>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <TimelineTime>{moment(flowItem.timeStamp).format("DD/MM/YYYY HH:mm")}</TimelineTime>
                      <StatusBadge status={flowItem.status === 'Closed' ? 'closed' : 'pending'}>
                        {flowItem.status === 'Closed' ? 'CLOSED' : 'PENDING'}
                      </StatusBadge>
                    </div>
                  </TimelineHeader>

                  {(flowItem?.action || flowItem?.status) && (
                    <div style={{
                      marginBottom: '15px',
                      padding: '10px 12px',
                      background: flowItem?.status === 'Closed' ? '#e8f5e9' : '#f0f8ff',
                      borderRadius: '4px',
                      borderLeft: `3px solid ${flowItem?.status === 'Closed' ? '#4caf50' : '#1976d2'
                        }`,
                      fontSize: '16px',
                      lineHeight: '1.5',
                      textAlign: 'center',
                      fontWeight: 'bold'
                    }}>
                      {flowItem.action || flowItem.status}
                    </div>
                  )}

                  {flowItem.remarks && (
                    <Remark>
                      <h4>Remark</h4>
                      <div>{flowItem.remarks}</div>
                    </Remark>
                  )}

                  <UserInfo>
                    {flowItem.action && <div><strong>Action:</strong> {flowItem.action}</div>}
                    {flowItem.actionUser && <div><strong>Action by:</strong> {flowItem.actionUser}</div>}
                    {flowItem.forwardedBy && <div><strong>Forwarded by:</strong> {flowItem.forwardedBy}</div>}
                  </UserInfo>
                </TimelineCard>
              </TimelineItemContainer>
            ))}
          </TimelineContainer>
        </div>
      )}

      {/* Action Section */}
      <ActionContainer>
        {!showActionOptions ? (
          <ButtonsWrapper>
            {/* For users: show action on closed complaints within escalation window */}
            {subTabValue === 0 && isComplaintClosed() && isComplaintForUser() && (
              <Button onClick={handleTakeAction}>Raise Grievance</Button>
            )}
            {/* For other roles: show Take Action only if not closed */}
            {subTabValue !== 0 && !isComplaintClosed() && isComplaintForUser() && (
              <Button onClick={handleTakeAction}>Take Action</Button>
            )}
          </ButtonsWrapper>
        ) : (
          <>
            {renderRoleSpecificForm()}
          </>
        )}
      </ActionContainer>
    </Container>
  );
};

export default ComplaintDetails; 